import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { CSSResultGroup } from "lit";
import baseStyles from "../../baseStyles.css";
import hostStyles from "./BuiToc.css";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
import ExpandCircleDown from "@material-symbols/svg-400/rounded/expand_circle_down.svg?raw";

const ExpandCircleDownIcon = unsafeSVG(ExpandCircleDown);

@customElement("bui-toc")
export class BuiToc extends LitElement {
  @property({ type: String }) title = "Table of Contents";
  @property({ type: Array }) headers: Array<{ id: string; text: string | null }> = [];
  @property({ type: String }) query = ".main-content h2";
  @property({ type: String, attribute: "show-text" }) showText = "Show All";

  static styles = [baseStyles, hostStyles] as CSSResultGroup;

  connectedCallback() {
    super.connectedCallback();
    this.updateHeaders();
  }

  updateHeaders() {
    const elems = Array.from(document.querySelectorAll(this.query));
    if (elems.length === 0) {
      this.remove(); // tear yourself out if nothing to show
      return;
    }
    this.headers = elems.map((el, i) => {
      const id = `toc-title-${i}`;
      el.id = id;
      return { id, text: el.textContent };
    });
  }

  private expand() {
    const toc = this.shadowRoot!.querySelector<HTMLElement>(".content-toc")!;
    toc.style.maxHeight = "max-content";
    this.shadowRoot!.querySelector<HTMLElement>(".content-toc__show-all")?.remove();
  }

  render() {
    // if no headers, render nothing
    if (this.headers.length === 0) {
      return nothing;
    }

    return html`
      <div class="content-toc-container" part="toc-container">
        <div class="content-toc-wrapper">
          <nav class="content-toc">
            <span class="content-toc-title">${this.title}</span>
            ${repeat(
              this.headers,
              (h) => h.id,
              (h) => html`<a href="#${h.id}" class="content-toc__jumplink">${h.text}</a>`
            )}
          </nav>
          ${when(this.headers.length >= 6, () => html` <button @click=${this.expand} class="content-toc__show-all">${ExpandCircleDownIcon} ${this.showText}</button> `)}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bui-toc": BuiToc;
  }
}
